import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import NavBar from "../components/navBar";
import { checkSubscriptionStatus } from "../services/subscriptionService";

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    email: localStorage.getItem("email") || "", // Get email from localStorage
    phone: "",
    operator: "mtn", // default to MTN
  });
  const [paymentStatus, setPaymentStatus] = useState("idle"); // idle, processing, success, error
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("token") !== null;

  useEffect(() => {
    const referenceId = localStorage.getItem("referenceId"); // 🔑 poll from storage
    const selectedPlan = JSON.parse(localStorage.getItem("selectedPlan"));

    if (!referenceId) return; // nothing to verify

    const verifyPayment = async () => {
      try {
        const verifyResponse = await axios.get(
          `https://payment-lenco-api.onrender.com/api/payment/verify/${referenceId}`
        );

        if (
          verifyResponse.data.status === true &&
          verifyResponse.data.data.status === "successful"
        ) {
          // ✅ Payment succeeded
          const startDate = new Date();
          const endDate = new Date();

          // set duration
          const duration = selectedPlan?.duration;
          if (duration === "Daily") {
            endDate.setDate(endDate.getDate() + 1);
          } else if (duration === "Weekly") {
            endDate.setDate(endDate.getDate() + 7);
          } else if (duration === "Monthly") {
            endDate.setMonth(endDate.getMonth() + 1);
          } else if (duration === "3 Months") {
            endDate.setMonth(endDate.getMonth() + 3);
          }

          // format dates
          const formattedStartDate = startDate.toISOString().split("T")[0];
          const formattedEndDate = endDate.toISOString().split("T")[0];

          // update subscription
          await axios.put(
            `${process.env.REACT_APP_API_URL}/subscriptions/${referenceId}`,
            {
              startDate: formattedStartDate,
              endDate: formattedEndDate,
              active: true,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          setPaymentStatus("success");
          setTimeout(() => {
            setShowPaymentModal(false);
            navigate("/");
          }, 2000);
        } else {
          // keep polling until success
          setTimeout(verifyPayment, 5000);
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
      }
    };

    // 🚀 Start verification loop
    verifyPayment();
  }, [navigate, selectedPlan]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch subscription plans
        const plansResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/subscription-plans`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setPlans(plansResponse.data);

        // Check subscription status if user is authenticated
        if (isAuthenticated) {
          const status = await checkSubscriptionStatus();
          // If the token is removed (user was logged out), navigate to home
          if (!localStorage.getItem("token")) {
            navigate("/");
            return;
          }
          setSubscriptionStatus(status);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        // If the token is removed (user was logged out), navigate to home
        if (!localStorage.getItem("token")) {
          navigate("/");
          return;
        }
        setError("Failed to load subscription plans");
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, navigate]);

  const generateReference = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    return `desac-${timestamp}-${randomNum}`;
  };

  const handleSubscribe = (plan) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentStatus("processing");

    try {
      const referenceId = generateReference();

      // 1. Create subscription record in database
      const subscriptionResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscriptions/`,
        {
          planId: selectedPlan.planId,
          referenceId,
          userId: localStorage.getItem("userId"),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // 2. Initiate payment with Lenco
      const paymentResponse = await axios.post(
        "https://payment-lenco-api.onrender.com/api/payment/request",
        {
          amount: parseFloat(selectedPlan.price.replace(/[^0-9.]/g, "")),
          reference: referenceId,
          phone: paymentDetails.phone,
          operator: paymentDetails.operator,
          country: "zm",
          bearer: "customer",
        }
      );

      // save referenceId for polling
      localStorage.setItem("referenceId", referenceId);
      localStorage.setItem("selectedPlan", JSON.stringify(selectedPlan));

      // 3. Start polling for payment verification
      const verifyPayment = async () => {
        try {
          const verifyResponse = await axios.get(
            `https://payment-lenco-api.onrender.com/api/payment/verify/${referenceId}`
          );

          if (
            verifyResponse.data.status === true &&
            verifyResponse.data.data.status === "successful"
          ) {
            // Format dates for the update request
            const startDate = new Date();
            const endDate = new Date();

            // Calculate end date based on plan duration
            const duration = selectedPlan.duration;
            if (duration === "Daily") {
              endDate.setDate(endDate.getDate() + 1);
            } else if (duration === "Weekly") {
              endDate.setDate(endDate.getDate() + 7);
            } else if (duration === "Monthly") {
              endDate.setMonth(endDate.getMonth() + 1);
            } else if (duration === "3 Months") {
              endDate.setMonth(endDate.getMonth() + 3);
            }

            // Format dates as YYYY-MM-DD
            const formattedStartDate = startDate.toISOString().split("T")[0];
            const formattedEndDate = endDate.toISOString().split("T")[0];

            // Update subscription status
            await axios.put(
              `${process.env.REACT_APP_API_URL}/subscriptions/${referenceId}`,
              {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                active: true,
              },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );

            setPaymentStatus("success");
            setTimeout(() => {
              setShowPaymentModal(false);
              navigate("/");
            }, 2000);
          } else {
            // Continue polling if payment is still pending
            setTimeout(verifyPayment, 5000);
          }
        } catch (error) {
          console.error("Error verifying payment:", error);
          setPaymentStatus("error");
        }
      };

      // Start verification polling
      verifyPayment();
    } catch (error) {
      console.error("Error processing payment:", error);
      setPaymentStatus("error");
    }
  };

  if (loading)
    return (
      <div className="text-white text-center p-8">
        Loading subscription plans...
      </div>
    );
  if (error) return <div className="text-white text-center p-8">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-900">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8 text-center">
          Subscription Plans
        </h1>

        {/* Subscription status message */}
        {isAuthenticated &&
          subscriptionStatus &&
          !subscriptionStatus.isValid && (
            <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 mb-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                No Active Subscription
              </h2>
              <p className="text-gray-300 mb-4">
                You don't have an active subscription. Please subscribe to one
                of the plans below to access our premium content.
              </p>
              {subscriptionStatus.error && (
                <p className="text-red-400 text-sm">
                  {subscriptionStatus.error}
                </p>
              )}
            </div>
          )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.planId}
              className="bg-gray-800 rounded-lg p-6 shadow-lg"
            >
              <h2 className="text-2xl font-bold text-white mb-4">
                {plan.planName}
              </h2>
              <p className="text-gray-300 mb-4">{plan.duration}</p>
              <p className="text-3xl font-bold text-red-500 mb-6">
                {plan.price}
              </p>

              <ul className="text-gray-300 mb-8 space-y-2">
                {plan.features &&
                  plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg
                        className="w-5 h-5 text-green-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      {feature.feature}
                    </li>
                  ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan)}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
              >
                Subscribe Now
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">
              Complete Your Subscription
            </h2>
            <form onSubmit={handlePaymentSubmit}>
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  required
                  className="w-full bg-gray-700 text-white rounded-lg p-2"
                  value={paymentDetails.email}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      email: e.target.value,
                    })
                  }
                  readOnly // Make it read-only since it's from localStorage
                />
                <p className="text-gray-400 text-sm mt-1">
                  Email is automatically filled from your account
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  required
                  className="w-full bg-gray-700 text-white rounded-lg p-2"
                  value={paymentDetails.phone}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      phone: e.target.value,
                    })
                  }
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">
                  Payment Method
                </label>
                <select
                  className="w-full bg-gray-700 text-white rounded-lg p-2"
                  value={paymentDetails.operator}
                  onChange={(e) =>
                    setPaymentDetails({
                      ...paymentDetails,
                      operator: e.target.value,
                    })
                  }
                >
                  <option value="mtn">MTN Mobile Money</option>
                  <option value="airtel">Airtel Money</option>
                </select>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={paymentStatus === "processing"}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg"
                >
                  {paymentStatus === "processing" ? "Processing..." : "Pay Now"}
                </button>
              </div>
              {paymentStatus === "error" && (
                <p className="text-red-400 mt-4">
                  Payment failed. Please try again.
                </p>
              )}
              {paymentStatus === "success" && (
                <p className="text-green-400 mt-4">
                  Payment successful! Redirecting...
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;